import { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import cn from '../utils/cn';
import KoobAuthCard from './KoobAuthCard';
import KoobButton from './KoobButton';
import KoobInputText from './KoobInputText';

const TABS = ['dmc', 'to'];

export default function KoobLoginCard({
  onSubmit,
  forgotPasswordUrl,
  defaultTab = 'dmc',
  onTabChange,
  signUpUrl,
  t,
}) {
  const [activeTab, setActiveTab] = useState(defaultTab);

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    onTabChange?.(tab);
  };

  const validationSchema = Yup.object().shape({
    username: Yup.string().email().required(t('errors:required')),
    password: Yup.string().required(t('errors:required')),
  });

  return (
    <KoobAuthCard
      title={t('auth:login.title')}
      subtitle={t('auth:login.subtitle')}
    >
      <div className="bg-navy-50 rounded-[10px] p-1 flex mb-5">
        {TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => handleTabChange(tab)}
            className={cn(
              'flex-1 py-2 text-sm font-semibold rounded-lg transition-all border-none cursor-pointer',
              activeTab === tab
                ? 'bg-white shadow-sm text-k-h'
                : 'text-k-p hover:text-k-h bg-transparent',
            )}
          >
            {tab.toUpperCase()}
          </button>
        ))}
      </div>

      <Formik
        initialValues={{ username: '', password: '' }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, errors, touched }) => (
          <Form>
            <div className="space-y-5">
              <div>
                <label
                  htmlFor="login-username"
                  className="block text-[13px] font-semibold text-k-h mb-1.5"
                >
                  {t('auth:email')}
                </label>
                <Field
                  as={KoobInputText}
                  id="login-username"
                  name="username"
                  type="email"
                  autoComplete="email"
                  tabIndex={1}
                  error={Boolean(errors.username && touched.username)}
                />
                {errors.username && touched.username && (
                  <p className="mt-1 text-sm text-red-500">{errors.username}</p>
                )}
              </div>

              <div>
                <div className="flex items-baseline justify-between mb-1.5">
                  <label
                    htmlFor="login-password"
                    className="block text-[13px] font-semibold text-k-h"
                  >
                    {t('auth:password')}
                  </label>
                  {forgotPasswordUrl && (
                    <a
                      href={forgotPasswordUrl}
                      className="text-[13px] font-semibold text-orange-700 no-underline hover:text-k-h"
                      tabIndex={3}
                    >
                      {t('auth:login.forgotPassword')}
                    </a>
                  )}
                </div>
                <Field
                  as={KoobInputText}
                  id="login-password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  tabIndex={1}
                  error={Boolean(errors.password && touched.password)}
                />
                {errors.password && touched.password && (
                  <p className="mt-1 text-sm text-red-500">{errors.password}</p>
                )}
              </div>
            </div>

            <div className="mt-5">
              <KoobButton
                type="submit"
                variant="navy"
                size="lg"
                block
                loading={isSubmitting}
                tabIndex={2}
                id="login-button"
              >
                {t('auth:login.button')}
              </KoobButton>
            </div>
          </Form>
        )}
      </Formik>

      {signUpUrl && (
        <div className="mt-5 text-center text-sm text-k-p">
          {t('auth:login.noAccount')}{' '}
          <a
            href={signUpUrl}
            className="font-semibold text-orange-700 no-underline hover:text-k-h"
            data-testid="signin"
          >
            {t('auth:signUp.button')}
          </a>
        </div>
      )}
    </KoobAuthCard>
  );
}
